import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { Container } from "reactstrap";
import styled from "styled-components";
import ExportDeliveryNotice from "../../components/Reports/ExportDeliveryNotice";
import SourcesUsed from "../../components/Reports/SourcesUsed";
import {
  artifactQualityMessage,
  artifactUsable,
  exportDelivery,
  heldArtifactEntries,
  usesArtifactQualityProtocol,
} from "../../utils/artifactQuality";

import CoverPreview from "../../components/CoverPreview/CoverPreview";
import AddToListing, {
  artifactBought,
  isCreditsListing,
  useCreditsAccount,
} from "../../components/Credits/AddToListing";
import NewFooter from "../../components/Footers/NewFooter";
import NewNavbar from "../../components/Navbar/NewNavbar";
import ShareSnapshots from "../../components/ShareSnapshots/ShareSnapshots";
import SocialPost from "../../components/SocialPost/SocialPost";
import VideoPreviewCard from "../../components/VideoPreviewCard/VideoPreviewCard";
import { API_URL } from "../../constants";
import { formatCredits } from "../../constants/credits";
import useAuthenticatedVideoBlob from "../../hooks/useAuthenticatedVideoBlob";
import usePollReportDocument from "../../hooks/usePollReportDocument";
import useVideoPreview, {
  approvedFinalVideo,
  videoPreviewStatus,
} from "../../hooks/useVideoPreview";
import { track } from "../../utils/analytics";
import { authHeader } from "../../utils/auth";
import { ExportLibraryLoadError, loadZipLibrary } from "../../utils/exportLibraries";
import { jobFailureMessage, reportJobId } from "../../utils/reportJobs";
import { absoluteVideoUrl } from "../../utils/videoPreview";

/** @typedef {{artifactKey: string, legacyField: string, key: string, filename: string, mime: string}} ShareOutput */
/** @typedef {{uuid: string, documentVersion: number, url: string, preparedAt: number, outputKey: string, artifactKey: string, legacyField: string}} PreparedNativeShare */

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #eef5f4 0%, #f9fafb 320px);
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 120px 0 70px;
`;

const Title = styled.h1`
  font-size: 1.9rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 6px;
  letter-spacing: -0.01em;
  overflow-wrap: anywhere;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 0.98rem;
  color: #6b7280;
  margin: 0 0 24px;
`;

const RenderBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 20px;
  font-size: 0.92rem;
  color: #92400e;
`;

const PublishError = styled(RenderBanner)`
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  background: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
`;

const RetryButton = styled.button`
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid #991b1b;
  border-radius: 999px;
  background: #ffffff;
  color: #991b1b;
  font-weight: 700;
  cursor: pointer;
`;

const InlineError = styled.p`
  margin: 8px 0 0;
  color: #b91c1c;
  font-size: 0.82rem;
`;

const ShareLinkInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  min-height: 44px;
  margin-top: 12px;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #f9fafb;
  color: #374151;
  font-size: 0.86rem;
`;

const Spinner = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #fcd34d;
  border-top-color: #92400e;
  display: inline-block;
  flex-shrink: 0;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// The page reads as one centered column: the cover scorecard keeps its
// designed card width, centered, and the section content centers with it.
const OutputGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 240px), 1fr));
  gap: 14px;
  margin-bottom: 30px;
`;

const OutputActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
`;

const SocialPostText = styled.div`
  font-size: 0.92rem;
  line-height: 1.5;
  color: #1a1a1a;
  white-space: pre-line;
  margin-bottom: 10px;
`;

const CopyButton = styled.button`
  font-size: 0.82rem;
  font-weight: 600;
  color: #374151;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0 14px;
  min-height: 44px;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
  }
`;

const ReadyHeader = styled.div`
  text-align: center;
  margin-bottom: 26px;
`;

const ReadyPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: var(--brand-primary-50, #e6f3f2);
  color: var(--brand-primary-700, #085552);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 6px 14px;
  margin-bottom: 12px;
`;

const MetaLine = styled.p`
  font-size: 0.95rem;
  color: #6b7280;
  margin: 6px 0 0;
`;

const Section = styled.section`
  background: #fff;
  border: 1px solid var(--brand-border, #e3ebec);
  border-radius: 16px;
  padding: 24px 26px;
  margin-bottom: 16px;

  @media (max-width: 767.98px) {
    padding: 20px 18px;
  }
`;

const SectionHead = styled.h2`
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--brand-ink, #0b1c1e);
  margin: 20px 0 4px;
`;

const SectionSub = styled.p`
  font-size: 0.92rem;
  color: #6b7280;
  margin: 0 0 18px;
`;

// Watermark mention #1 of two (the export moment): one calm line, no badges.
const WatermarkNote = styled.p`
  font-size: 0.88rem;
  color: #6b7280;
  margin: 12px 0 0;

  a {
    color: var(--brand-primary-700, #085552);
    font-weight: 600;
    text-decoration: underline;
  }
`;

const DownloadCard = styled.div`
  border: 1px solid ${(props) =>
    props.$featured ? "var(--brand-primary, #0e7e7a)" : "var(--brand-border, #e3ebec)"};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DownloadCardTitle = styled.h3`
  font-size: 0.98rem;
  font-weight: 600;
  color: var(--brand-ink, #0b1c1e);
  margin: 0;
`;

const DownloadCardDesc = styled.p`
  font-size: 0.82rem;
  line-height: 1.45;
  color: #6b7280;
  margin: 0;
  flex: 1;
`;

const AppendixBar = styled.div`
  margin-top: 18px;
  padding: 14px 16px;
  border: 1px dashed #d1d5db;
  border-radius: 12px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  font-size: 0.9rem;
  color: #374151;

  strong {
    font-weight: 600;
  }

  label {
    display: inline-block;
    padding: 7px 16px;
    border-radius: 50px;
    background: #ffffff;
    border: 1px solid #0e7e7a;
    color: #0e7e7a;
    font-weight: 600;
    cursor: pointer;
  }

  input[type="file"] {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    overflow: hidden;
  }
`;

const AppendixRemove = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  font-size: 0.85rem;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AppendixError = styled.p`
  width: 100%;
  margin: 0;
  color: #991b1b;
  font-size: 0.85rem;
`;

const FormatRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`;

const FormatButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid
    ${(props) => (props.$primary ? "var(--brand-primary, #0e7e7a)" : "var(--brand-border-strong, #cfd9db)")};
  background: ${(props) => (props.$primary ? "var(--brand-primary, #0e7e7a)" : "#fff")};
  color: ${(props) => (props.$primary ? "#fff" : "var(--brand-ink, #0b1c1e)")};
  border-radius: 8px;
  padding: 7px 13px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// const PersonalizePrompt = styled.div`
//   display: flex;
//   align-items: center;
//   justify-content: space-between;
//   gap: 14px;
//   flex-wrap: wrap;
//   margin-top: 18px;
//   padding-top: 16px;
//   border-top: 1px solid var(--brand-border, #e3ebec);
// `;

// const PersonalizePromptText = styled.span`
//   font-size: 0.88rem;
//   color: #4a4a4a;
// `;

const PersonalizeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: var(--brand-primary-50, #e6f3f2);
  color: var(--brand-primary-700, #085552);
  border: none;
  border-radius: var(--brand-radius-lg, 10px);
  padding: 10px 18px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: var(--brand-primary-100, #c9e4e2);
  }
`;

const PhotoTeaserStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 20px;
`;

const PhotoTeaserThumb = styled.div`
  width: 160px;
  height: 120px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid var(--brand-border, #e3ebec);
  background: var(--brand-bg-soft, #f7fafb);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

// const PhotoTeaserButton = styled.button`
//   display: inline-flex;
//   align-items: center;
//   gap: 8px;
//   background: #fff;
//   border: 1px solid var(--brand-primary, #0e7e7a);
//   color: var(--brand-primary-700, #085552);
//   border-radius: var(--brand-radius-lg, 10px);
//   padding: 11px 20px;
//   font-size: 0.92rem;
//   font-weight: 600;
//   cursor: pointer;
//   transition: background 0.15s ease;

//   &:hover {
//     background: var(--brand-primary-50, #e6f3f2);
//   }
// `;

const PhotoActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
`;

const ListingActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
`;

const ListingActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #fff;
  border: 1px solid var(--brand-border, #d1d5db);
  color: #0b1c1e;
  border-radius: 999px;
  padding: 8px 14px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f3f4f6;
  }
`;

const PhotoActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${(props) => (props.$primary ? "var(--brand-primary, #0e7e7a)" : "#fff")};
  border: 1px solid
    ${(props) => (props.$primary ? "var(--brand-primary, #0e7e7a)" : "var(--brand-border, #d1d5db)")};
  color: ${(props) => (props.$primary ? "#fff" : "#0b1c1e")};
  border-radius: 10px;
  padding: 11px 20px;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${(props) => (props.$primary ? "var(--brand-primary-700, #085552)" : "#f3f4f6")};
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

// The thumbnails are always the originals, so whether anything was touched up
// is the one thing an agent cannot read off the strip. It gets its own band
// instead of a clause in the section copy, and it renders in every state —
// a silent strip is what made agents think enhancement did not exist.
const PhotoEnhancementNote = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 4px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid
    ${(props) =>
      props.$highlight ? "var(--brand-primary, #0e7e7a)" : "var(--brand-border, #e3ebec)"};
  background: ${(props) =>
    props.$highlight ? "var(--brand-primary-50, #e6f3f2)" : "var(--brand-bg-soft, #f7fafb)"};
`;

const PhotoEnhancementBadge = styled.span`
  flex: 0 0 auto;
  border-radius: 999px;
  padding: 4px 11px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(props) => (props.$highlight ? "var(--brand-primary-700, #085552)" : "#4b5563")};
  background: ${(props) => (props.$highlight ? "#fff" : "#eef2f3")};
`;

const PhotoEnhancementText = styled.p`
  flex: 1 1 260px;
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.5;
  color: #374151;
`;

// Social content sits on the package page itself, so its two halves — the
// captions and the snapshot cards — need a heading rung under SectionHead.
const SubHead = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: var(--brand-ink, #0b1c1e);
  margin: 22px 0 4px;
`;

const CoverHero = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
`;

const PersonalizeBar = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 12px auto 24px;
  background: #fff;
  border: 1px solid var(--brand-border, #e3ebec);
  border-radius: 12px;
  padding: 16px 18px;
  text-align: center;
`;

const PersonalizeBarText = styled.p`
  font-size: 1.15rem;
  line-height: 1.5;
  color: #4a4a4a;
  margin: 0 0 12px;
`;

const CoverKicker = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${(p) => p.$accent || "#0e7e7a"};
  
`;

const CenteredMessage = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #4a4a4a;
`;

// New report-document responses include a persisted artifact manifest. When
// present, it is authoritative: never expose a theoretical URL for an exporter
// that failed. Legacy responses still use the URL fields directly.
/**
 * @param {Record<string, any>} doc
 * @param {string} key
 * @param {string} legacyField
 */
const artifactUrl = (doc, key, legacyField) => {
  if (!doc.artifacts) return doc[legacyField] || null;
  const artifact = doc.artifacts[key];
  if (!artifactUsable(artifact, key) || !artifact.url) return null;
  return artifact.url;
};

/** @param {{artifacts?: {report_pdf?: {available?: boolean}}} | null | undefined} doc */
const hasPublishedPdf = (doc) => doc?.artifacts?.report_pdf?.available === true;

/**
 * Is the current document published for sharing? A legacy listing shipped
 * with its PDF, so the PDF manifest entry is the proof. A credits listing's
 * deliverable is the web document (CREDITS_FLOW_DESIGN v3 §1/§6: the share
 * link is bound to rendered_version, not to a file) — files are rendered only
 * when bought, so gating on the PDF would rerender forever for a listing that
 * never bought one.
 * @param {Record<string, any> | null | undefined} doc
 */
const hasPublishedOutputs = (doc) =>
  isCreditsListing(doc) || usesArtifactQualityProtocol(doc)
    ? (doc?.rendered_version || 0) >= (doc?.version || 0)
    : hasPublishedPdf(doc);
const PREPARED_NATIVE_SHARE_MAX_AGE_MS = 10_000;

/**
 * Trust only the server-provided canonical route when a gallery is available.
 * Owner galleries can also contain only failed attempts.
 * @param {Record<string, any>} doc
 */
const availableTouchups = (doc) => {
  const touchups = doc?.photo_touchups;
  if (
    touchups?.available !== true ||
    typeof touchups.path !== "string" ||
    !/^\/touchups\/[0-9a-f-]{36}$/i.test(touchups.path)
  ) {
    return null;
  }
  const rawCount = touchups.count;
  const count =
    typeof rawCount === "number" || (typeof rawCount === "string" && rawCount.trim())
      ? Number(rawCount)
      : Number.NaN;
  if (!Number.isSafeInteger(count) || count < 0) return null;
  return { path: touchups.path, count };
};

/**
 * Photo review records the edits it wants per room on the PhotoAnalysisSection,
 * but a PhotoTouchupSet only exists once at least one edit was found — so
 * "reviewed, nothing worth editing" and "review never ran" both arrive as
 * photo_touchups.available === false. The sections are the only signal that
 * separates them, and without it the photos area goes silent over originals it
 * never labels as such.
 * @param {Record<string, any>} doc
 * @returns {{kind: "enhanced" | "attempts", count: number, path: string}
 *   | {kind: "no_edits_needed" | "edits_unavailable" | "not_run"}}
 */
const photoEnhancement = (doc) => {
  const touchups = availableTouchups(doc);
  if (touchups) return { kind: touchups.count > 0 ? "enhanced" : "attempts", ...touchups };
  const sections = Array.isArray(doc?.content?.sections) ? doc.content.sections : [];
  const reviewed = sections.filter(
    (/** @type {Record<string, any>} */ section) => section?.__type__ === "PhotoAnalysisSection"
  );
  if (reviewed.length === 0) return { kind: "not_run" };
  // Edits were flagged yet no gallery exists: the touch-up pipeline was off or
  // failed. Say the photos are untouched rather than imply an enhancement.
  const flagged = reviewed.some(
    (/** @type {Record<string, any>} */ section) =>
      (section.assessment?.machine_edits || []).length > 0
  );
  return { kind: flagged ? "edits_unavailable" : "no_edits_needed" };
};

/**
 * @param {ReturnType<typeof photoEnhancement>} enhancement
 * @param {boolean} [creditsListing] Touch-ups are added on tap (v3) — never
 *   "unavailable", just not chosen yet.
 */
const photoEnhancementCopy = (enhancement, creditsListing = false) => {
  if (
    creditsListing &&
    (enhancement.kind === "edits_unavailable" || enhancement.kind === "not_run")
  ) {
    return {
      badge: "Originals",
      text: "These are your original photos. Touch-ups can be added from “Add to this listing” above.",
    };
  }
  switch (enhancement.kind) {
    case "enhanced":
      return {
        badge: "Touched up",
        text: `We cleaned up ${enhancement.count} ${enhancement.count === 1 ? "photo" : "photos"}. The thumbnails above are your originals — open the before-and-after gallery to compare each edit and download the ones you want to use.`,
      };
    case "attempts":
      return {
        badge: "Originals only",
        text: "No edited photos are currently available. Your originals are unchanged. Review the touch-up results and retry options.",
      };
    case "no_edits_needed":
      return {
        badge: "Reviewed",
        text: "We checked these photos for touch-ups — decluttering, brightening, tidying up the yard — and none of them needed editing. They're ready to use as they are.",
      };
    case "edits_unavailable":
      return {
        badge: "Originals only",
        text: "We flagged touch-ups for some of these photos, but the cleaned-up versions aren't available for this listing. Everything above is your untouched original.",
      };
    default:
      return {
        badge: "Originals only",
        text: "Photo touch-ups — decluttering, brightening, tidying up the yard — didn't run for this listing, so these are your untouched originals.",
      };
  }
};

/**
 * A prepared URL can bypass network work on the next click only while it is
 * still bound to this route, exact document version, and available output.
 * @param {PreparedNativeShare | null} prepared
 * @param {string} uuid
 * @param {Record<string, any> | null} doc
 * @param {string} outputKey
 */
const preparedNativeShareIsCurrent = (prepared, uuid, doc, outputKey) => {
  if (!prepared || !doc || prepared.uuid !== uuid || prepared.outputKey !== outputKey) return false;
  const version = Number(doc.version) || 0;
  const age = Date.now() - prepared.preparedAt;
  return (
    version > 0 &&
    prepared.documentVersion === version &&
    (Number(doc.rendered_version) || 0) >= version &&
    age >= 0 &&
    age <= PREPARED_NATIVE_SHARE_MAX_AGE_MS &&
    !!artifactUrl(doc, prepared.artifactKey, prepared.legacyField)
  );
};
/** @param {string} message */
const localActionError = (message) => Object.assign(new Error(message), { safeMessage: message });

const staleRouteActionError = () =>
  Object.assign(new Error("The report route changed while the action was running."), {
    staleRoute: true,
  });

/** @param {unknown} error */
const isStaleRouteActionError = (error) =>
  /** @type {{staleRoute?: unknown}} */ (error)?.staleRoute === true;

/** @param {unknown} error @param {string} fallback */
const safeActionMessage = (error, fallback) => {
  const typed =
    /** @type {{safeMessage?: unknown, response?: {status?: unknown, data?: {error?: unknown}}}} */ (
      error
    );
  if (typed.response?.status === 401) {
    return "Your session expired. Sign in again, then try again.";
  }
  if (typed.response?.status === 403) {
    return "You no longer have access to this listing. Sign in as the owner, then try again.";
  }
  if (typed.response?.status === 404) {
    return "That file is no longer available. Refresh the page, then try again.";
  }
  const apiError = typed.response?.data?.error;
  if (
    typed.response?.status === 409 &&
    typeof apiError === "string" &&
    apiError.length > 0 &&
    apiError.length <= 240
  ) {
    return apiError;
  }
  return typeof typed.safeMessage === "string" ? typed.safeMessage : fallback;
};

/** @param {string} url @param {string} filename */
const triggerBrowserDownload = (url, filename) => {
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/** @param {string} address */
const slugifyForZip = (address) => {
  const cleaned = (address || "listing")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return cleaned || "listing";
};

/**
 * @param {Record<string, string>} photoManifest
 * @param {string} address
 * @param {() => boolean} isCurrent
 */
const downloadAllPhotos = async (photoManifest, address, isCurrent) => {
  const photoKeys = Object.keys(photoManifest || {});
  if (photoKeys.length === 0) return;

  const JSZip = await loadZipLibrary();
  if (!isCurrent()) return;
  const zip = new JSZip();
  for (const key of photoKeys) {
    const url = photoManifest[key];
    if (!url) continue;
    const response = await fetch(url, { cache: "reload" });
    if (!isCurrent()) return;
    if (!response.ok) throw new Error(`Failed to fetch ${key}`);
    const blob = await response.blob();
    if (!isCurrent()) return;
    zip.file(key, blob);
  }
  const zipBlob = await zip.generateAsync({ type: "blob" });
  if (!isCurrent()) return;
  const blobUrl = URL.createObjectURL(zipBlob);

  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = `${slugifyForZip(address)}-photos.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
};

const SharePage = () => {
  /** @type {{ uuid: string }} */
  const { uuid } = useParams();
  const history = useHistory();
  // Update during render, before effect cleanup, so an old request resolving in
  // the route-change gap cannot write into the next report's state.
  const activeUuidRef = useRef(uuid);
  activeUuidRef.current = uuid;
  // State remains mounted when only :uuid changes. Tag the loaded snapshot so
  // the previous report is ignored during the render-before-effect-cleanup
  // gap, including by the automatic publish effect below.
  const loadedUuidRef = useRef(/** @type {string | null} */ (null));
  const [docState, setDoc] = useState(/** @type {Record<string, any> | null} */ (null));
  const [statusState, setStatus] = useState(
    /** @type {"loading" | "ok" | "not_found" | "error"} */ ("loading")
  );
  const routeStateIsCurrent = loadedUuidRef.current === uuid;
  const doc = routeStateIsCurrent ? docState : null;
  const status = routeStateIsCurrent ? statusState : "loading";
  const [rendering, setRendering] = useState(false);
  // Keep activation and job identity atomic so immediate polling never runs a
  // legacy document-only tick between two related state updates.
  const [renderPoll, setRenderPoll] = useState(
    /** @type {{active: boolean, jobId: string | null, uuid: string | null, baseVersion: number}} */ ({
      active: false,
      jobId: null,
      uuid: null,
      baseVersion: 0,
    })
  );
  const [renderError, setRenderError] = useState(/** @type {string | null} */ (null));
  const [renderAttempt, setRenderAttempt] = useState(0);
  const [appendixBusy, setAppendixBusy] = useState(false);
  const [appendixError, setAppendixError] = useState(/** @type {string | null} */ (null));
  const [actionError, setActionError] = useState(/** @type {string | null} */ (null));
  const [actionBusy, setActionBusy] = useState(/** @type {string | null} */ (null));

  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState(/** @type {string | null} */ (null));
  const photoFlightRef = useRef(/** @type {object | null} */ (null));
  const photoVersionRef = useRef(doc?.version);
  photoVersionRef.current = doc?.version;
  useEffect(() => {
    if (activeUuidRef.current !== uuid || photoVersionRef.current !== doc?.version) return;
    photoFlightRef.current = null;
    setPhotoBusy(false);
    setPhotoError(null);
    return () => {
      photoFlightRef.current = null;
    };
  }, [uuid, doc?.version]);
  const [publicShareUrl, setPublicShareUrl] = useState("");
  const [publicShareVersion, setPublicShareVersion] = useState(0);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [linkStatus, setLinkStatus] = useState("");
  const [linkRetry, setLinkRetry] = useState(
    /** @type {{uuid: string, attempted: boolean} | null} */ (null)
  );
  const sharedRef = useRef(false);
  const publishConflictRef = useRef("");
  const linkRecoveryCancelledRef = useRef(false);
  const preparedNativeShareRef = useRef(/** @type {PreparedNativeShare | null} */ (null));
  const [videoDeliveryBusy, setVideoDeliveryBusy] = useState(/** @type {string | null} */ (null));
  const [videoDeliveryError, setVideoDeliveryError] = useState("");
  const videoPreview = useVideoPreview({
    uuid,
    active: status === "ok" && !!doc?.editable,
  });
  const refreshVideoPreview = videoPreview.refresh;
  // Pricing v2: the balance for the spend sheets (flag-aware; no fetch when off).
  const account = useCreditsAccount();
  const accountRefresh = account.refresh;
  // A bought file or the social set landed (Add to this listing): adopt the
  // owner snapshot; a newer unpublished version goes back through publishing.
  const adoptOwnerDocument = useCallback(
    (/** @type {Record<string, any>} */ fresh) => {
      if (activeUuidRef.current !== uuid) return;
      setDoc(fresh);
      if ((fresh.version || 0) > (fresh.rendered_version || 0)) {
        setRenderAttempt((attempt) => attempt + 1);
      }
    },
    [uuid]
  );
  const currentVideoStatus = videoPreviewStatus(videoPreview.preview);
  const currentOwnerFinalVideo = videoPreview.preview?.final_video;
  const ownerMediaSource =
    videoPreview.preview?.is_stale !== true &&
    ["awaiting_review", "approved"].includes(currentVideoStatus) &&
    currentOwnerFinalVideo?.available === true
      ? currentOwnerFinalVideo.play_url || ""
      : "";
  const ownerPlayback = useAuthenticatedVideoBlob({
    sourceUrl: ownerMediaSource,
    active: !!ownerMediaSource,
    cacheKey: [
      uuid,
      doc?.version || 0,
      videoPreview.preview?.uuid || videoPreview.preview?.id || "",
      currentOwnerFinalVideo?.media_id || "",
    ].join(":"),
  });

  /**
   * Pin every share-page behavior to the report and published document version.
   * @param {string} event
   * @param {Record<string, string | number | boolean>} [properties]
   */
  const trackShare = useCallback(
    (
      /** @type {string} */ event,
      /** @type {Record<string, string | number | boolean>} */ properties = {}
    ) => {
      track(event, {
        ...properties,
        computation_uuid: uuid,
        document_version: doc?.version || 0,
      });
    },
    [uuid, doc?.version]
  );

  useEffect(() => {
    let cancelled = false;
    const requestUuid = uuid;
    // React Router keeps this component mounted when only :uuid changes. Clear
    // every report-scoped value before loading the next document so the prior
    // listing, public link, errors, and publish job cannot leak into the new
    // route.
    loadedUuidRef.current = requestUuid;
    setDoc(null);
    setStatus("loading");
    setRendering(false);
    setRenderPoll({ active: false, jobId: null, uuid: null, baseVersion: 0 });
    setRenderError(null);
    setRenderAttempt(0);
    setActionError(null);
    setActionBusy(null);
    setVideoDeliveryBusy(null);
    setVideoDeliveryError("");
    setPublicShareUrl("");
    setPublicShareVersion(0);
    setLinkBusy(false);
    setLinkError("");
    setLinkStatus("");
    setLinkRetry(null);
    sharedRef.current = false;
    publishConflictRef.current = "";
    linkRecoveryCancelledRef.current = false;
    preparedNativeShareRef.current = null;
    (async () => {
      try {
        const resp = await axios.get(`${API_URL}/computations/${uuid}/report-document/`, {
          headers: authHeader(),
        });
        if (cancelled || activeUuidRef.current !== requestUuid) return;
        loadedUuidRef.current = requestUuid;
        setDoc(resp.data);
        setStatus("ok");
        track("share_opened", {
          computation_uuid: requestUuid,
          document_version: resp.data.version || 0,
          was_stale: (resp.data.version || 0) > (resp.data.rendered_version || 0),
        });
      } catch (err) {
        if (cancelled || activeUuidRef.current !== requestUuid) return;
        const sc = /** @type {{ response?: { status?: number } }} */ (err).response?.status;
        loadedUuidRef.current = requestUuid;
        setStatus(sc === 404 ? "not_found" : "error");
      }
    })();
    return () => {
      cancelled = true;
      if (activeUuidRef.current === requestUuid) activeUuidRef.current = "";
    };
  }, [uuid]);

  // A prepared capability is useful only for an immediate second click. Any
  // route or owner-document change revalidates its exact version/readiness and
  // drops it when the report moved or its output is no longer available.
  useEffect(() => {
    const prepared = preparedNativeShareRef.current;
    if (!prepared) return;
    if (!preparedNativeShareIsCurrent(prepared, uuid, doc, prepared.outputKey)) {
      preparedNativeShareRef.current = null;
    }
  }, [doc, uuid]);

  // Append documents (Trello #3): attach/remove the agent's own PDF and
  // republish. An appendix edit does NOT bump the document version, so the
  // automatic stale-publish effect below won't fire — kick the same
  // owner-only rerender + poll explicitly.
  const republishForAppendix = useCallback(async () => {
    try {
      const response = await axios.post(
        `${API_URL}/computations/${uuid}/rerender/`,
        {},
        { headers: authHeader() }
      );
      setRenderError(null);
      setRendering(true);
      setRenderPoll({
        active: true,
        jobId: reportJobId(response.data),
        uuid,
        baseVersion: Number(response.data?.version) || 0,
      });
    } catch {
      setAppendixError(
        "Saved — but publishing the updated package didn't start. Reload the page to retry."
      );
    }
  }, [uuid]);

  /** @param {React.ChangeEvent<HTMLInputElement>} e */
  const handleAppendixSelected = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setAppendixBusy(true);
    setAppendixError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API_URL}/computations/${uuid}/appendix/`, formData, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      setDoc((prev) => (prev ? { ...prev, appendix: response.data?.appendix || null } : prev));
      await republishForAppendix();
    } catch (err) {
      const apiError = /** @type {{response?: {data?: {error?: string}}}} */ (err).response?.data
        ?.error;
      setAppendixError(apiError || "That PDF couldn't be attached. Try again.");
    } finally {
      setAppendixBusy(false);
    }
  };

  const handleAppendixRemove = async () => {
    setAppendixBusy(true);
    setAppendixError(null);
    try {
      await axios.delete(`${API_URL}/computations/${uuid}/appendix/`, { headers: authHeader() });
      setDoc((prev) => (prev ? { ...prev, appendix: null } : prev));
      await republishForAppendix();
    } catch {
      setAppendixError("The document couldn't be removed. Try again.");
    } finally {
      setAppendixBusy(false);
    }
  };

  // Auto-save defers rendering, so the package can be stale (version >
  // rendered_version) when Share opens. Kick one re-render and poll until the
  // outputs are current, so downloads always reflect the latest edits.
  // OWNER-ONLY: /rerender/ is owner-gated and only the owner's edits can make
  // the doc stale — a non-owner viewer just gets the last-rendered files
  // immediately instead of a spinner waiting on a job that can never start.
  // biome-ignore lint/correctness/useExhaustiveDependencies: doc is intentionally the loaded snapshot; renderAttempt is the explicit retry trigger.
  useEffect(() => {
    if (status !== "ok" || !doc || !doc.editable) return undefined;
    const requestUuid = uuid;
    const stale = (doc.version || 0) > (doc.rendered_version || 0);
    // A legacy/current document can still be unshareable when it predates the
    // artifact manifest. The API requires the current PDF manifest entry, so
    // heal that state through the same owner-only publish path instead of
    // enabling a button that is guaranteed to receive a 409.
    if (!stale && hasPublishedOutputs(doc)) return undefined;
    setPublicShareUrl("");
    setPublicShareVersion(0);
    setRenderError(null);
    setRenderPoll({ active: false, jobId: null, uuid: null, baseVersion: 0 });
    setRendering(true);
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.post(
          `${API_URL}/computations/${uuid}/rerender/`,
          { expected_version: doc.version },
          { headers: authHeader() }
        );
        if (cancelled || activeUuidRef.current !== requestUuid) return;
        if (response.data?.job_status === "failed") {
          setRenderError(
            jobFailureMessage(null, "Publishing your latest edits could not be started.")
          );
          setRendering(false);
          track("share_publish_failed", {
            computation_uuid: uuid,
            document_version: doc.version || 0,
            stage: "start",
            status: 0,
          });
          return;
        }
        setRenderPoll({
          active: true,
          jobId: reportJobId(response.data),
          uuid,
          baseVersion: Number(response.data?.version) || Number(doc.version) || 0,
        });
      } catch (err) {
        if (cancelled || activeUuidRef.current !== requestUuid) return;
        const response = /** @type {{ status?: number, data?: {error?: unknown} } | undefined} */ (
          /** @type {{ response?: unknown }} */ (err).response
        );
        const sc = response?.status;
        const apiMessage = typeof response?.data?.error === "string" ? response.data.error : "";
        if (sc === 409) {
          try {
            const refreshed = await axios.get(`${API_URL}/computations/${uuid}/report-document/`, {
              headers: authHeader(),
            });
            if (cancelled || activeUuidRef.current !== requestUuid) return;
            const freshDoc = refreshed.data || {};
            const recoveryKey = `${requestUuid}:${doc.version || 0}:${freshDoc.version || 0}`;
            setDoc(freshDoc);
            if (
              (freshDoc.version || 0) > (doc.version || 0) &&
              publishConflictRef.current !== recoveryKey
            ) {
              publishConflictRef.current = recoveryKey;
              setRenderPoll({ active: false, jobId: null, uuid: null, baseVersion: 0 });
              setRendering(false);
              setRenderAttempt((attempt) => attempt + 1);
              return;
            }
          } catch {
            if (cancelled || activeUuidRef.current !== requestUuid) return;
          }
        }
        setRenderError(
          sc === 401 || sc === 403
            ? "Your session can no longer publish this listing. Sign in again, then retry."
            : jobFailureMessage(
                { message: apiMessage },
                "We couldn't publish your latest edits. Previous files remain blocked so you don't share stale work."
              )
        );
        setRenderPoll({ active: false, jobId: null, uuid: null, baseVersion: 0 });
        setRendering(false);
        track("share_publish_failed", {
          computation_uuid: uuid,
          document_version: doc.version || 0,
          stage: "start",
          status: sc || 0,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, renderAttempt]);

  // Poll until the outputs catch up with the latest saved edits.
  usePollReportDocument({
    active: renderPoll.active && renderPoll.uuid === uuid,
    uuid,
    jobId: renderPoll.jobId,
    isReady: (data) =>
      (data.rendered_version || 0) >= renderPoll.baseVersion && hasPublishedOutputs(data),
    onReady: (data) => {
      if (activeUuidRef.current !== uuid) return;
      setDoc(data);
      setRenderError(null);
      setRenderPoll({ active: false, jobId: null, uuid: null, baseVersion: 0 });
      setRendering(false);
      if ((data.version || 0) > (data.rendered_version || 0)) {
        setRenderAttempt((attempt) => attempt + 1);
      }
    },
    onFailed: (failure) => {
      if (activeUuidRef.current !== uuid) return;
      const requestUuid = uuid;
      const failedBaseVersion = renderPoll.baseVersion;
      setRenderPoll({ active: false, jobId: null, uuid: null, baseVersion: 0 });
      const finishFailedPublish = () => {
        if (activeUuidRef.current !== requestUuid) return;
        setRendering(false);
        setRenderError(jobFailureMessage(failure, "Publishing your latest edits failed."));
        trackShare("share_publish_failed", { stage: "worker" });
      };
      if (failure?.code !== "stale_version") {
        finishFailedPublish();
        // A terminal export failure may still publish the web analysis and
        // retain usable same-version files. Refresh metadata, never restart it.
        void axios
          .get(`${API_URL}/computations/${requestUuid}/report-document/`, {
            headers: authHeader(),
            timeout: 30000,
          })
          .then((response) => {
            if (activeUuidRef.current === requestUuid) setDoc(response.data || {});
          })
          .catch(() => {});
        return;
      }

      // A worker can reject a queued publish after another tab saves a newer
      // document version. Refresh the owner snapshot and let the normal publish
      // effect retry that newer version once. The same transition key is used
      // by the publish-start 409 recovery above, so the two paths cannot form
      // independent retry loops for one edit transition.
      setRenderError(null);
      setRendering(true);
      void (async () => {
        try {
          const refreshed = await axios.get(
            `${API_URL}/computations/${requestUuid}/report-document/`,
            { headers: authHeader() }
          );
          if (activeUuidRef.current !== requestUuid) return;
          const freshDoc = refreshed.data || {};
          const freshVersion = Number(freshDoc.version) || 0;
          const recoveryKey = `${requestUuid}:${failedBaseVersion}:${freshVersion}`;
          setDoc(freshDoc);
          if (freshVersion > failedBaseVersion && publishConflictRef.current !== recoveryKey) {
            publishConflictRef.current = recoveryKey;
            setRendering(false);
            setRenderAttempt((attempt) => attempt + 1);
            return;
          }
        } catch {
          if (activeUuidRef.current !== requestUuid) return;
        }
        finishFailedPublish();
      })();
    },
    onTimeout: () => {
      if (activeUuidRef.current !== uuid) return;
      setRenderPoll({ active: false, jobId: null, uuid: null, baseVersion: 0 });
      setRendering(false);
      setRenderError(
        "Publishing is taking longer than expected. Downloads stay blocked until the latest version is ready."
      );
      trackShare("share_publish_failed", { stage: "timeout" });
    },
  });

  // Record only deliberate sharing completion (public link or a native share
  // sheet that resolved). Downloads, saved snapshots, and copied post text have
  // their own analytics but do not advance the dashboard to "Shared".
  const markShared = useCallback(() => {
    if (activeUuidRef.current !== uuid) return;
    if (sharedRef.current) return;
    sharedRef.current = true;
    axios
      .post(`${API_URL}/computations/${uuid}/mark-shared/`, {}, { headers: authHeader() })
      .catch(() => {
        if (activeUuidRef.current === uuid) sharedRef.current = false;
      });
  }, [uuid]);

  /**
   * Refresh the owner document immediately before an artifact action. The API
   * issues five-minute presigned URLs, so the page-load snapshot is only for
   * availability/display and is never used as the action target.
   * @param {ShareOutput} output
   */
  const refreshOwnerArtifact = useCallback(
    async (/** @type {ShareOutput} */ output) => {
      const requestUuid = uuid;
      const response = await axios.get(`${API_URL}/computations/${uuid}/report-document/`, {
        headers: authHeader(),
      });
      if (activeUuidRef.current !== requestUuid) throw staleRouteActionError();
      const freshDoc = response.data || {};
      setDoc(freshDoc);
      if ((freshDoc.version || 0) > (freshDoc.rendered_version || 0)) {
        setRenderAttempt((attempt) => attempt + 1);
        throw localActionError(
          "Your latest edits are still publishing. Wait for publishing to finish, then try again."
        );
      }
      const url = artifactUrl(freshDoc, output.artifactKey, output.legacyField);
      if (!url) {
        throw localActionError(
          "That file is not available in the latest package. Refresh the page, then try again."
        );
      }
      return { freshDoc, url };
    },
    [uuid]
  );

  const requestPublicShareLink = useCallback(async () => {
    const requestUuid = uuid;
    const response = await axios.post(
      `${API_URL}/computations/${uuid}/create-share-link/`,
      {},
      { headers: authHeader() }
    );
    if (activeUuidRef.current !== requestUuid) throw staleRouteActionError();
    const url = response.data?.share_url || "";
    if (!url) throw localActionError("The share link was not returned. Try again.");
    return {
      url,
      documentVersion: Number(response.data?.document_version) || 0,
    };
  }, [uuid]);

  /**
   * @param {ShareOutput} output
   */
  const downloadArtifact = useCallback(
    async (/** @type {ShareOutput} */ output) => {
      const requestUuid = uuid;
      setActionError(null);
      setActionBusy(`download:${output.key}`);
      try {
        const { freshDoc, url } = await refreshOwnerArtifact(output);
        if (activeUuidRef.current !== requestUuid) return;
        triggerBrowserDownload(url, output.filename);
        track("share_download", {
          computation_uuid: uuid,
          document_version: freshDoc.version || 0,
          format: output.key,
        });
      } catch (error) {
        if (isStaleRouteActionError(error) || activeUuidRef.current !== requestUuid) return;
        setActionError(
          safeActionMessage(
            error,
            "Could not refresh that file. Check your connection, then try the download again."
          )
        );
      } finally {
        if (activeUuidRef.current === requestUuid) setActionBusy(null);
      }
    },
    [refreshOwnerArtifact, uuid]
  );

  /**
   * Share only a durable, revocable public report URL. Avoiding artifact blob
   * downloads keeps the preparation window short. If that async preparation
   * consumes browser activation, retain its exact version-bound URL briefly so
   * an immediate second click can invoke Web Share before any new await.
   * @param {ShareOutput} output
   * @param {string} title
   */
  const nativeShare = useCallback(
    async (/** @type {ShareOutput} */ output, /** @type {string} */ title) => {
      const requestUuid = uuid;
      setActionError(null);
      setActionBusy(`share:${output.key}`);
      try {
        if (!navigator.share) {
          preparedNativeShareRef.current = null;
          throw localActionError(
            "Sharing is not available in this browser. Download the file and attach it instead."
          );
        }
        const prepared = preparedNativeShareRef.current;
        if (prepared && preparedNativeShareIsCurrent(prepared, requestUuid, doc, output.key)) {
          // This call intentionally happens before any await in this click so
          // the browser's transient user activation is still available.
          await navigator.share({ title, url: prepared.url });
          if (activeUuidRef.current !== requestUuid) return;
          if (preparedNativeShareRef.current === prepared) preparedNativeShareRef.current = null;
          markShared();
          track("share_native_share", {
            computation_uuid: uuid,
            document_version: prepared.documentVersion,
            format: output.key || "unknown",
          });
          return;
        }
        preparedNativeShareRef.current = null;

        const { freshDoc } = await refreshOwnerArtifact(output);
        const freshVersion = Number(freshDoc.version) || 0;
        const cachedShareUrl =
          publicShareUrl && publicShareVersion === freshVersion ? publicShareUrl : "";
        if (publicShareUrl && !cachedShareUrl) {
          setPublicShareUrl("");
          setPublicShareVersion(0);
        }
        let durableUrl = cachedShareUrl;
        let durableVersion = freshVersion;
        if (!durableUrl) {
          const publicLink = await requestPublicShareLink();
          if (activeUuidRef.current !== requestUuid) throw staleRouteActionError();
          setPublicShareUrl(publicLink.url);
          durableUrl = publicLink.url;
          durableVersion = publicLink.documentVersion || freshVersion;
          setPublicShareVersion(durableVersion);
          setLinkStatus("");
        }
        if (activeUuidRef.current !== requestUuid) throw staleRouteActionError();
        const preparedShare = {
          uuid: requestUuid,
          documentVersion: durableVersion,
          url: durableUrl,
          preparedAt: Date.now(),
          outputKey: output.key,
          artifactKey: output.artifactKey,
          legacyField: output.legacyField,
        };
        preparedNativeShareRef.current = preparedShare;
        await navigator.share({ title, url: durableUrl });
        if (activeUuidRef.current !== requestUuid) return;
        if (preparedNativeShareRef.current === preparedShare) {
          preparedNativeShareRef.current = null;
        }
        markShared();
        track("share_native_share", {
          computation_uuid: uuid,
          document_version: durableVersion,
          format: output.key || "unknown",
        });
      } catch (error) {
        if (/** @type {{ name?: string }} */ (error).name !== "NotAllowedError") {
          preparedNativeShareRef.current = null;
        }
        if (
          !isStaleRouteActionError(error) &&
          activeUuidRef.current === requestUuid &&
          /** @type {{ name?: string }} */ (error).name !== "AbortError"
        ) {
          setActionError(
            safeActionMessage(
              error,
              "Could not open sharing. Download the file and attach it instead."
            )
          );
        }
      } finally {
        if (activeUuidRef.current === requestUuid) setActionBusy(null);
      }
    },
    [
      doc,
      markShared,
      publicShareUrl,
      publicShareVersion,
      refreshOwnerArtifact,
      requestPublicShareLink,
      uuid,
    ]
  );

  const createPublicShareLink = useCallback(
    async (automaticRetry = false) => {
      const requestUuid = uuid;
      if (automaticRetry && linkRecoveryCancelledRef.current) return;
      if (!automaticRetry) linkRecoveryCancelledRef.current = false;
      setLinkBusy(true);
      setLinkError("");
      setLinkStatus("");
      try {
        const publicLink = await requestPublicShareLink();
        if (
          activeUuidRef.current !== requestUuid ||
          (automaticRetry && linkRecoveryCancelledRef.current)
        )
          return;
        setPublicShareUrl(publicLink.url);
        setPublicShareVersion(publicLink.documentVersion || doc?.version || 0);
        setLinkStatus("");
        setLinkRetry(null);
        markShared();
        trackShare("share_public_link_created");
        try {
          if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
          await navigator.clipboard.writeText(publicLink.url);
        } catch {
          if (activeUuidRef.current !== requestUuid) return;
          setLinkError("The link is ready below, but could not be copied automatically.");
        }
      } catch (error) {
        if (isStaleRouteActionError(error) || activeUuidRef.current !== requestUuid) return;
        const responseStatus = /** @type {{response?: {status?: number}}} */ (error).response
          ?.status;
        if (responseStatus === 409) {
          preparedNativeShareRef.current = null;
          setPublicShareUrl("");
          setPublicShareVersion(0);
        }
        let retryAfterPublish = false;
        if (responseStatus === 409 && !automaticRetry) {
          try {
            const refreshed = await axios.get(`${API_URL}/computations/${uuid}/report-document/`, {
              headers: authHeader(),
            });
            if (activeUuidRef.current !== requestUuid) return;
            setDoc(refreshed.data);
            setRenderAttempt((attempt) => attempt + 1);
            retryAfterPublish = true;
          } catch {
            if (activeUuidRef.current !== requestUuid) return;
            // Keep the curated conflict reason visible; a later retry can
            // refresh again when connectivity returns.
          }
        }
        setLinkRetry(retryAfterPublish ? { uuid: requestUuid, attempted: false } : null);
        if (retryAfterPublish) {
          // Publishing state already communicates progress. Do not show the
          // old terminal error while the guarded automatic retry is active.
          setLinkError("");
        } else {
          setLinkError(
            responseStatus === 409
              ? "Could not create a share link. Make sure publishing finished, then try again."
              : safeActionMessage(
                  error,
                  "Could not create a share link. Make sure publishing finished, then try again."
                )
          );
        }
      } finally {
        if (activeUuidRef.current === requestUuid) setLinkBusy(false);
      }
    },
    [doc?.version, markShared, requestPublicShareLink, trackShare, uuid]
  );

  // A create-link conflict can mean this tab missed a newer publish. Refresh
  // above, let the normal publish flow make that exact version ready, then try
  // link creation once. Marking the retry attempted before starting it prevents
  // a persistent 409 from becoming a request loop.
  useEffect(() => {
    if (
      !linkRetry ||
      linkRetry.uuid !== uuid ||
      linkRetry.attempted ||
      linkRecoveryCancelledRef.current
    )
      return;
    const publishReady =
      status === "ok" &&
      !!doc &&
      (doc.rendered_version || 0) >= (doc.version || 0) &&
      hasPublishedOutputs(doc) &&
      !rendering &&
      !renderPoll.active &&
      !linkBusy;
    if (!publishReady) return;
    setLinkRetry({ ...linkRetry, attempted: true });
    setLinkError("");
    void createPublicShareLink(true);
  }, [createPublicShareLink, doc, linkBusy, linkRetry, renderPoll.active, rendering, status, uuid]);

  const revokePublicShareLinks = useCallback(async () => {
    const requestUuid = uuid;
    linkRecoveryCancelledRef.current = true;
    preparedNativeShareRef.current = null;
    setLinkRetry(null);
    setPublicShareUrl("");
    setPublicShareVersion(0);
    setLinkBusy(true);
    setLinkError("");
    setLinkStatus("");
    try {
      await axios.post(
        `${API_URL}/computations/${uuid}/revoke-share-links/`,
        {},
        { headers: authHeader() }
      );
      if (activeUuidRef.current !== requestUuid) return;
      setPublicShareUrl("");
      setPublicShareVersion(0);
      setLinkStatus("All public links are revoked.");
    } catch (error) {
      if (activeUuidRef.current !== requestUuid) return;
      setLinkError(
        safeActionMessage(
          error,
          "Could not revoke public links. Check your connection, then retry."
        )
      );
    } finally {
      if (activeUuidRef.current === requestUuid) setLinkBusy(false);
    }
  }, [uuid]);

  const refreshApprovedVideo = useCallback(async () => {
    const requestUuid = uuid;
    const snapshot = await refreshVideoPreview();
    if (activeUuidRef.current !== requestUuid) throw staleRouteActionError();
    const finalVideo = approvedFinalVideo(snapshot.preview);
    if (!finalVideo) {
      throw localActionError(
        "This walkthrough is no longer approved and current. Refresh the page before sharing it."
      );
    }
    return finalVideo;
  }, [refreshVideoPreview, uuid]);

  const downloadVideoPreview = useCallback(async () => {
    const requestUuid = uuid;
    let blobUrl = "";
    setVideoDeliveryBusy("download");
    setVideoDeliveryError("");
    try {
      const finalVideo = await refreshApprovedVideo();
      const mediaUrl = finalVideo.download_url || finalVideo.play_url;
      if (!mediaUrl) throw localActionError("The approved video file is not available.");
      const response = await axios.get(absoluteVideoUrl(mediaUrl), {
        headers: authHeader(),
        responseType: "blob",
      });
      if (activeUuidRef.current !== requestUuid) throw staleRouteActionError();
      if (typeof URL.createObjectURL !== "function") {
        throw localActionError("This browser cannot prepare the video download.");
      }
      blobUrl = URL.createObjectURL(response.data);
      triggerBrowserDownload(
        blobUrl,
        `${slugifyForZip(doc?.address || "listing")}-walkthrough.mp4`
      );
    } catch (videoError) {
      if (isStaleRouteActionError(videoError) || activeUuidRef.current !== requestUuid) return;
      setVideoDeliveryError(
        safeActionMessage(
          videoError,
          "Could not refresh the approved video. Check your connection, then try again."
        )
      );
    } finally {
      if (blobUrl && typeof URL.revokeObjectURL === "function") {
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      }
      if (activeUuidRef.current === requestUuid) setVideoDeliveryBusy(null);
    }
  }, [doc?.address, refreshApprovedVideo, uuid]);

  const copyVideoShareLink = useCallback(async () => {
    const requestUuid = uuid;
    setVideoDeliveryBusy("share");
    setVideoDeliveryError("");
    try {
      await refreshApprovedVideo();
      const publicLink = await requestPublicShareLink();
      if (activeUuidRef.current !== requestUuid) throw staleRouteActionError();
      setPublicShareUrl(publicLink.url);
      setPublicShareVersion(publicLink.documentVersion || doc?.version || 0);
      setLinkError("");
      setLinkStatus("");
      markShared();
      trackShare("share_public_link_created");
      if (!navigator.clipboard?.writeText) {
        throw localActionError(
          "The link is ready in the Share section below, but could not be copied."
        );
      }
      await navigator.clipboard.writeText(publicLink.url);
      if (activeUuidRef.current !== requestUuid) return;
      setLinkStatus("Walkthrough share link copied.");
    } catch (videoError) {
      if (isStaleRouteActionError(videoError) || activeUuidRef.current !== requestUuid) return;
      setVideoDeliveryError(
        safeActionMessage(
          videoError,
          "Could not prepare a current walkthrough share link. Try again."
        )
      );
    } finally {
      if (activeUuidRef.current === requestUuid) setVideoDeliveryBusy(null);
    }
  }, [doc?.version, markShared, refreshApprovedVideo, requestPublicShareLink, trackShare, uuid]);

  const retryPublish = useCallback(async () => {
    const requestUuid = uuid;
    setRenderError(null);
    setRendering(true);
    try {
      const refreshed = await axios.get(`${API_URL}/computations/${uuid}/report-document/`, {
        headers: authHeader(),
      });
      if (activeUuidRef.current !== requestUuid) return;
      setDoc(refreshed.data || {});
      setRenderAttempt((attempt) => attempt + 1);
    } catch {
      if (activeUuidRef.current !== requestUuid) return;
      setRendering(false);
      setRenderError(
        "We couldn't publish your latest edits. Previous files remain blocked so you don't share stale work."
      );
    }
  }, [uuid]);

  let body;
  if (status === "loading") {
    body = (
      <CenteredMessage as="output" aria-live="polite">
        <p>Loading your listing package…</p>
      </CenteredMessage>
    );
  } else if (status === "not_found") {
    body = (
      <CenteredMessage>
        <h1>Nothing to share yet</h1>
        <p>This link may have expired or the listing isn't ready.</p>
      </CenteredMessage>
    );
  } else if (status === "error" || !doc) {
    body = (
      <CenteredMessage>
        <h1>Something went wrong</h1>
        <p>
          We couldn't load your listing package. Try refreshing, or email{" "}
          <a href="mailto:help@listino.ai">help@listino.ai</a>.
        </p>
      </CenteredMessage>
    );
  } else {
    const canShare = typeof navigator !== "undefined" && !!navigator.share;
    const stale = (doc.version || 0) > (doc.rendered_version || 0);
    const downloadsBlocked =
      !!doc.editable &&
      (stale ||
        !hasPublishedOutputs(doc) ||
        (!usesArtifactQualityProtocol(doc) && (rendering || !!renderError)));
    const creditsListing = isCreditsListing(doc);
    const heldExports =
      heldArtifactEntries(doc).length > 0 ||
      !!exportDelivery(doc)?.units.some((/** @type {any} */ unit) => unit.status === "held");
    const outputs = [
      {
        key: "pdf",
        card: "report",
        formatLabel: "PDF",
        artifactKey: "report_pdf",
        legacyField: "report_pdf_url",
        url: artifactUrl(doc, "report_pdf", "report_pdf_url"),
        filename: "listing-strategy.pdf",
        mime: "application/pdf",
        label: "Listing Strategy",
        desc: "The complete listing package: analysis, pricing, and photo direction.",
      },
      {
        key: "deck",
        card: "presentation",
        formatLabel: "PDF",
        artifactKey: "deck",
        legacyField: "deck_pdf_url",
        url: artifactUrl(doc, "deck", "deck_pdf_url"),
        filename: "listing-slides.pdf",
        mime: "application/pdf",
        label: "Seller presentation",
        desc: "Slides to present to your seller or send along.",
      },
      {
        key: "pptx",
        card: "presentation",
        formatLabel: "PowerPoint",
        artifactKey: "pptx",
        legacyField: "pptx_url",
        url: artifactUrl(doc, "pptx", "pptx_url"),
        filename: "listing-prep.pptx",
        mime: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        label: "Seller presentation",
        desc: "Slides to present to your seller or send along.",
      },
      {
        key: "one_pager",
        card: "one_pager",
        formatLabel: "PDF",
        artifactKey: "one_pager",
        legacyField: "one_pager_pdf_url",
        url: artifactUrl(doc, "one_pager", "one_pager_pdf_url"),
        filename: "listing-one-pager.pdf",
        mime: "application/pdf",
        label: "One-pager",
        desc: "A single page to hand to the seller.",
      },
    ];
    // Group formats of the same deliverable into one card (e.g. presentation
    // as both PDF and PowerPoint), preserving first-seen order.
    /** @type {{ card: string; label: string; desc: string; formats: typeof outputs }[]} */
    const downloadCards = [];
    /** @type {Record<string, { card: string; label: string; desc: string; formats: Array<typeof outputs[number]> }>} */
    const cardByKey = {};
    for (const output of outputs) {
      // Never surface a format whose exporter failed. artifactUrl() returns
      // null when the persisted manifest marks it unavailable, and a card
      // built from it renders a button that downloads nothing.
      if (!output.url) continue;
      if (!cardByKey[output.card]) {
        cardByKey[output.card] = {
          card: output.card,
          label: output.label,
          desc: output.desc,
          formats: [],
        };
        downloadCards.push(cardByKey[output.card]);
      }
      cardByKey[output.card].formats.push(output);
    }

    const photoManifest = /** @type {Record<string, string>} */ (doc.photo_manifest || {});
    const photoNames = Object.keys(photoManifest);
    const enhancement = photoEnhancement(doc);
    const enhancementCopy = photoEnhancementCopy(enhancement, creditsListing);
    const shownPhotoCount = Math.min(photoNames.length, 6);
    const originalPhotoNoun = photoNames.length === 1 ? "photo" : "photos";
    const photoDownloadBusy = photoBusy;
    const socialPosts = /** @type {{ label: string, text: string }[]} */ (doc.social_posts || []);

    body = (
      <>
        <ReadyHeader>
          <ReadyPill>Listing results</ReadyPill>
          <Title>{doc.address || "Your listing"}</Title>
          <MetaLine>
            {heldExports ? "Your listing analysis is ready." : "Your listing package is ready."}
          </MetaLine>
        </ReadyHeader>

        {rendering && (
          <RenderBanner as="output" aria-live="polite">
            <Spinner />
            Applying your latest edits to the listing package — downloads will be current in a
            moment.
          </RenderBanner>
        )}
        {renderError && (
          <PublishError role="alert">
            <span>{renderError}</span>
            <RetryButton type="button" onClick={retryPublish}>
              Retry publish
            </RetryButton>
          </PublishError>
        )}
        {actionError && (
          <PublishError role="alert">
            <span>{actionError}</span>
            <RetryButton type="button" onClick={() => setActionError(null)}>
              Dismiss
            </RetryButton>
          </PublishError>
        )}

        <CoverHero>
          <CoverPreview
            doc={doc}
            sections={doc.content?.sections || []}
            branding={doc.branding || {}}
            showViewDoc={false}
          />
        </CoverHero>

        {doc.editable && (
          <PersonalizeBar>
            <PersonalizeBarText>
              AI-generated analysis — verify the property facts, pricing, and recommendations before
              sharing.
            </PersonalizeBarText>
            <ListingActions>
              <PersonalizeButton
                type="button"
                onClick={() => history.push(`/reports/${uuid}/edit`)}
              >
                <span aria-hidden="true">✦</span> Review &amp; personalize
              </PersonalizeButton>

              <ListingActionButton
                type="button"
                onClick={() => history.push(`/reports/${uuid}/edit?open=reprepare`)}
              >
                Re-run Quick CMA
                {creditsListing &&
                  doc.reprepare &&
                  (Number(doc.reprepare.cost) === 0
                    ? " · your free re-run"
                    : ` · ${formatCredits(doc.reprepare.cost)}`)}
              </ListingActionButton>
            </ListingActions>
          </PersonalizeBar>
        )}
        <Section aria-labelledby="seller-materials-heading">
          <CoverKicker>Listing Package</CoverKicker>

          <SectionHead id="seller-materials-heading">Seller Materials</SectionHead>
          <SectionSub>
            Review your available files before downloading and sharing — re-exporting is always
            free.
          </SectionSub>
          {doc.editable && <ExportDeliveryNotice doc={doc} />}
          <SourcesUsed doc={doc} />
          <OutputGrid>
            {downloadCards.map((card) => (
              <DownloadCard key={card.card} $featured={card.card === "report"}>
                <DownloadCardTitle>{card.label}</DownloadCardTitle>
                <DownloadCardDesc>{card.desc}</DownloadCardDesc>
                {card.formats.map((fmt) => (
                  <DownloadCardDesc key={fmt.key}>
                    {fmt.formatLabel}:{" "}
                    {artifactQualityMessage(doc.artifacts?.[fmt.artifactKey], fmt.artifactKey)}
                  </DownloadCardDesc>
                ))}
                <FormatRow>
                  {card.formats.map((fmt, i) =>
                    downloadsBlocked ? (
                      <FormatButton key={fmt.key} type="button" disabled aria-disabled="true">
                        Publishing…
                      </FormatButton>
                    ) : (
                      <FormatButton
                        key={fmt.key}
                        type="button"
                        $primary={card.card === "report" || i === 0}
                        disabled={!!actionBusy}
                        aria-disabled={!!actionBusy}
                        onClick={() => downloadArtifact(fmt)}
                      >
                        {actionBusy === `download:${fmt.key}` ? "Preparing…" : fmt.formatLabel}
                      </FormatButton>
                    )
                  )}
                  {canShare && !downloadsBlocked && (
                    <FormatButton
                      type="button"
                      disabled={!!actionBusy || linkBusy}
                      onClick={() =>
                        nativeShare(card.formats[0], `${doc.address || "Listing"} — ${card.label}`)
                      }
                    >
                      {actionBusy === `share:${card.formats[0].key}` ? "Preparing…" : "Share"}
                    </FormatButton>
                  )}
                </FormatRow>
              </DownloadCard>
            ))}
          </OutputGrid>
          {/* v3: nothing is rendered speculatively — a credits listing's
              un-bought files simply are not here, so there is nothing to
              "check back" for; the Add section below lists them. */}
          {downloadCards.length === 0 && !creditsListing && !usesArtifactQualityProtocol(doc) && (
            <Subtitle>
              Your Listing Strategy, seller presentation, and one-pager are being prepared. Check
              back in a moment.
            </Subtitle>
          )}
          {doc.watermarked === true && (
            <WatermarkNote data-testid="watermark-note">
              Files on the Free plan carry a small Listino mark.{" "}
              <Link to="/pricing">Upgrading</Link> removes it from everything you've prepared.
            </WatermarkNote>
          )}
          {doc.editable && (
            <AppendixBar>
              {doc.appendix ? (
                <>
                  <span>
                    Appended: <strong>{doc.appendix.filename}</strong>{" "}
                    {doc.appendix.page_count
                      ? `(${doc.appendix.page_count} page${doc.appendix.page_count === 1 ? "" : "s"})`
                      : ""}{" "}
                    — added at the end of the Listing Strategy PDF.
                  </span>
                  <label htmlFor="appendix-input">
                    {appendixBusy ? "Working…" : rendering ? "Publishing…" : "Replace"}
                  </label>
                  <AppendixRemove
                    type="button"
                    disabled={appendixBusy || rendering}
                    onClick={handleAppendixRemove}
                  >
                    Remove
                  </AppendixRemove>
                </>
              ) : (
                <>
                  <span>
                    Have disclosures, a floor plan, or an HOA packet? Append your own PDF — its
                    pages are added at the end of the Listing Strategy PDF.
                  </span>
                  <label htmlFor="appendix-input">
                    {appendixBusy ? "Working…" : rendering ? "Publishing…" : "Append a PDF"}
                  </label>
                </>
              )}
              {/* Disabled while a publish is in flight: a second job would
                  bounce off the one-job-at-a-time guard as a 409 that reads
                  like a failure. The bar re-enables when the poll completes. */}
              <input
                id="appendix-input"
                type="file"
                accept="application/pdf"
                disabled={appendixBusy || rendering}
                onChange={handleAppendixSelected}
              />
              {appendixError && <AppendixError role="alert">{appendixError}</AppendixError>}
            </AppendixBar>
          )}
        </Section>

        {doc.editable && (
          <Section aria-labelledby="seller-sharing-heading">
            <CoverKicker>Share</CoverKicker>
            <SectionHead id="seller-sharing-heading">Share with your seller</SectionHead>
            <SocialPostText>
              Create an owner-controlled web link to the current published version. You can revoke
              it at any time.
            </SocialPostText>
            <OutputActions>
              <CopyButton
                type="button"
                disabled={downloadsBlocked || linkBusy || !!actionBusy}
                onClick={() => createPublicShareLink(false)}
              >
                {linkBusy
                  ? "Working…"
                  : publicShareUrl && publicShareVersion === (doc.version || 0)
                    ? "Create a new link"
                    : "Create & copy link"}
              </CopyButton>
              <CopyButton
                type="button"
                disabled={linkBusy || !!actionBusy}
                onClick={revokePublicShareLinks}
              >
                Revoke all public links
              </CopyButton>
            </OutputActions>
            {publicShareUrl && publicShareVersion === (doc.version || 0) && (
              <ShareLinkInput
                readOnly
                aria-label="Share link"
                value={publicShareUrl}
                onFocus={(event) => event.target.select()}
              />
            )}
            {linkStatus && <SocialPostText as="output">{linkStatus}</SocialPostText>}
            {linkError && <InlineError role="alert">{linkError}</InlineError>}
          </Section>
        )}

        <AddToListing uuid={uuid} doc={doc} variant="share" onDocument={adoptOwnerDocument} />

        {(photoNames.length > 0 || socialPosts.length > 0) && (
          <Section aria-label="Listing photos and social content">
            {photoNames.length > 0 && (
              <div>
                {/* These come from doc.photo_manifest — the agent's own uploaded
                  or imported photos. Touch-ups live in a separate
                  PhotoTouchupSet and are never merged back into the manifest,
                  so this strip is never showing enhanced images and must not
                  claim to be. The note below the strip is the way to the
                  touched-up versions. */}
                <SectionHead>Your listing photos</SectionHead>
                <SectionSub>
                  Showing {shownPhotoCount} of {photoNames.length} original listing{" "}
                  {originalPhotoNoun}.
                  {enhancement.kind === "enhanced"
                    ? ` ${enhancement.count} enhanced photo${enhancement.count === 1 ? " is" : "s are"} ready to review.`
                    : ""}
                </SectionSub>
                <PhotoTeaserStrip>
                  {photoNames.slice(0, 6).map((name, i) => (
                    <PhotoTeaserThumb key={name}>
                      <img
                        src={photoManifest[name]}
                        alt={`${doc.address || "Listing"} — view ${i + 1}`}
                        loading="lazy"
                      />
                    </PhotoTeaserThumb>
                  ))}
                </PhotoTeaserStrip>
                <PhotoEnhancementNote $highlight={enhancement.kind === "enhanced"}>
                  <PhotoEnhancementBadge $highlight={enhancement.kind === "enhanced"}>
                    {enhancementCopy.badge}
                  </PhotoEnhancementBadge>
                  <PhotoEnhancementText>{enhancementCopy.text}</PhotoEnhancementText>
                  {(enhancement.kind === "enhanced" || enhancement.kind === "attempts") && (
                    <PhotoActionButton
                      type="button"
                      $primary
                      onClick={() => history.push(enhancement.path)}
                    >
                      {enhancement.kind === "enhanced"
                        ? "Review enhanced photos"
                        : "Review touch-up attempts"}
                    </PhotoActionButton>
                  )}
                </PhotoEnhancementNote>
                <PhotoActions>
                  <PhotoActionButton
                    type="button"
                    onClick={() => history.push(`/reports/${uuid}/edit?open=photos`)}
                  >
                    Add photos
                  </PhotoActionButton>
                  <PhotoActionButton
                    type="button"
                    disabled={photoDownloadBusy}
                    onClick={async () => {
                      if (photoDownloadBusy || photoFlightRef.current) return;
                      const flight = {};
                      photoFlightRef.current = flight;
                      const authorization = authHeader().Authorization;
                      const isCurrent = () =>
                        photoFlightRef.current === flight &&
                        activeUuidRef.current === uuid &&
                        photoVersionRef.current === doc.version &&
                        authHeader().Authorization === authorization;
                      setPhotoError(null);
                      setPhotoBusy(true);
                      try {
                        await downloadAllPhotos(photoManifest, doc.address || "listing", isCurrent);
                      } catch (error) {
                        if (isCurrent())
                          setPhotoError(
                            error instanceof ExportLibraryLoadError
                              ? error.message
                              : "Could not download photos. Check your connection, then try again."
                          );
                      } finally {
                        if (photoFlightRef.current === flight) {
                          photoFlightRef.current = null;
                          setPhotoBusy(false);
                        }
                      }
                    }}
                  >
                    {photoDownloadBusy ? "Preparing…" : "Download originals"}
                  </PhotoActionButton>
                </PhotoActions>
                {photoError && <InlineError role="alert">{photoError}</InlineError>}
              </div>
            )}
            {/* Captions come from the document and snapshots need photos — with
              neither there is nothing to show, and a heading over an empty
              block reads as a broken section. */}
            {(socialPosts.length > 0 || photoNames.length > 0) && (
              <div>
                <SectionHead>Social content</SectionHead>
                <SectionSub>
                  Captions and text-ready snapshots for this listing — copy a caption, pick a photo
                  and a look, then send or post it.
                </SectionSub>
                {socialPosts.length > 0 && (
                  <>
                    <SubHead>Captions</SubHead>
                    <SectionSub>Copy and paste into your listing post or a text.</SectionSub>
                    {socialPosts.map((post) => (
                      <SocialPost
                        key={post.label}
                        post={post}
                        onCopied={() =>
                          track("social_caption_copied", {
                            computation_uuid: uuid,
                            label: post.label,
                            document_version: doc.version || 0,
                          })
                        }
                      />
                    ))}
                  </>
                )}
                {/* ShareSnapshots renders nothing without listing photos, so the
                  heading is gated on the same condition to avoid an empty shell.
                  It is also part of the social set: these snapshots are social
                  assets, so a credits listing that has not bought the set does
                  not get them for free (Trello #76 — captions were gated and
                  these were not, which read as paid work being given away).
                  "Add to this listing" is where the set is offered. */}
                {photoNames.length > 0 && artifactBought(doc, "social_set") && (
                  <>
                    <SubHead>Text-ready snapshots</SubHead>
                    <SectionSub>
                      A picture of the home with the line that sells it, made to text a seller or
                      post.
                    </SectionSub>
                    <ShareSnapshots uuid={uuid} doc={doc} onShared={markShared} />
                  </>
                )}
              </div>
            )}
          </Section>
        )}

        {doc.editable && (
          <VideoPreviewCard
            compactWhenIdle
            enabled={videoPreview.enabled}
            phase={videoPreview.phase}
            preview={videoPreview.preview}
            generationLimits={videoPreview.generationLimits}
            error={videoPreview.error}
            action={videoPreview.action}
            startPending={videoPreview.startPending}
            startChecking={videoPreview.startChecking}
            startMessage={videoPreview.startMessage}
            startSettlement={videoPreview.startSettlement}
            onCheckStartStatus={videoPreview.checkStartStatus}
            pollTimedOut={videoPreview.pollTimedOut}
            externalBusy={videoDeliveryBusy}
            externalError={videoDeliveryError}
            playbackUrl={ownerPlayback.blobUrl}
            playbackLoading={ownerPlayback.loading}
            playbackError={ownerPlayback.error}
            credits={videoPreview.credits}
            creditsBlock={videoPreview.creditsBlock}
            balance={account.credits ? account.credits.balance : null}
            listingUuid={typeof doc.listing_uuid === "string" ? doc.listing_uuid : ""}
            onSpendOpen={accountRefresh}
            onDismissCreditsBlock={videoPreview.dismissCreditsBlock}
            onGenerate={async (clipCount) => {
              const result = await videoPreview.generate(clipCount, doc.version || 0);
              if (videoPreview.credits) void accountRefresh();
              return result;
            }}
            onApprove={() => videoPreview.approve(doc.version || 0)}
            onReject={() => videoPreview.reject(doc.version || 0)}
            onRetry={() => videoPreview.refresh()}
            onDownload={downloadVideoPreview}
            onShare={copyVideoShareLink}
          />
        )}
      </>
    );
  }

  return (
    <PageContainer>
      <NewNavbar />
      <ContentWrapper>
        <Container style={{ maxWidth: 1100 }}>{body}</Container>
      </ContentWrapper>
      <NewFooter />
    </PageContainer>
  );
};

export default SharePage;
